import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { criticalTranslations, loadTranslations } from '../i18n/loader';

const LanguageContext = createContext();
const supportedLanguages = ['en', 'tr'];

/**
 * Gets the initial language from URL params, localStorage, or defaults to 'en'
 * URL param takes priority to support language continuity from the website
 */
const getInitialLanguage = () => {
  // Check URL params first (for when users come from the website)
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');

    if (langParam && supportedLanguages.includes(langParam)) {
      // Store in localStorage for future visits
      localStorage.setItem('language', langParam);

      // Clean up URL param after reading (for cleaner URLs)
      urlParams.delete('lang');
      const newUrl = urlParams.toString()
        ? `${window.location.pathname}?${urlParams.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);

      return langParam;
    }
  }

  // Fall back to localStorage or default
  return localStorage.getItem('language') || 'en';
};

export const LanguageProvider = ({ children }) => {
  // Retrieve the language from URL params, localStorage, or default to 'en'
  const [lang, setLang] = useState(getInitialLanguage);

  // Start with critical translations for immediate render
  const [translations, setTranslations] = useState({
    en: criticalTranslations.en,
    tr: criticalTranslations.tr,
  });

  // Track if full translations are loaded
  const [isLoaded, setIsLoaded] = useState(false);

  // Load the full translation file for the current language
  useEffect(() => {
    let isMounted = true;

    const loadFullTranslations = async () => {
      try {
        const fullTranslations = await loadTranslations(lang);

        if (isMounted) {
          setTranslations(prev => ({
            ...prev,
            [lang]: fullTranslations,
          }));
          setIsLoaded(true);
        }
      } catch (error) {
        console.error('Failed to load translations:', error);
        setIsLoaded(true); // Still mark as loaded to prevent infinite loading
      }
    };

    loadFullTranslations();

    return () => {
      isMounted = false;
    };
  }, [lang]);

  useEffect(() => {
    // Save the selected language to localStorage whenever it changes
    localStorage.setItem('language', lang);
  }, [lang]);

  // Function to get the translated text with optional variable substitution
  const t = useCallback((key, params = {}) => {
    if (!key) return '';

    try {
      const keys = key.split('.');
      let result = translations[lang];

      // Check if the language exists
      if (!result) {
        result = translations['en'];

        // If English is also missing, return null to allow fallbacks
        if (!result) return null;
      }

      // Navigate through the keys
      for (const segment of keys) {
        result = result?.[segment];
        if (result === undefined) {
          // Key not found, return null to allow fallbacks
          return null;
        }
      }

      // Check if the result is an object (but allow arrays)
      if (typeof result === 'object' && !Array.isArray(result)) {
        return null;
      }

      // Handle string interpolation if result is a string and params are provided
      if (typeof result === 'string' && Object.keys(params).length > 0) {
        return result.replace(/\{(\w+)\}/g, (match, key) => {
          return params[key] !== undefined ? params[key] : match;
        });
      }

      return result;
    } catch (error) {
      return null;
    }
  }, [lang, translations]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, isLoaded }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
